import { Injectable, inject } from '@angular/core';
import { asyncScheduler, EMPTY } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  mergeMap,
  throttleTime,
  timeout,
  withLatestFrom
} from 'rxjs/operators';
import { createEffect } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import { zefGo } from '@zerops/zef/ngrx-router';
import { zefAddError } from '@zerops/zef/errors';
import { MergeStrategy } from '@zerops/zef/entities';
import { User } from '@zerops/models/user';
import {
  UserEntity,
  storeUserData,
  selectUserBaseActiveClientUserId
} from '@zerops/zerops/core/user-base';
// Leaf import (not the barrel): UserBaseApi isn't re-exported by
// @zerops/zerops/core/user-base's index.ts.
import { UserBaseApi } from '@zerops/zerops/core/user-base/user-base.api';
import { ClientUserEntity } from '@zerops/zerops/core/client-user-base';
import { LOGIN_ROUTE } from './app.constant';
import { confirmsRemoval, suspectsRemoval } from './client-user-removal';

const VERIFY_TIMEOUT_MS = 10_000;

// `list$`'s trailing `distinctUntilChanged` compares a freshly denormalized
// array each time, so it re-emits on ANY entities-slice update touching the
// collection. `exhaustMap` below bounds CONCURRENCY (one in-flight read);
// this bounds REPETITION. `trailing: true` so a suspicion raised inside an
// open window is re-evaluated at its close rather than dropped — the wipe
// must not depend on yet another entity update happening to arrive.
export const SUSPICION_THROTTLE_MS = 5_000;

/**
 * Owns the "user removed from client" wipe (previously the destructive branch
 * of `onClientUsersChanges$` in app.effect.ts).
 *
 * The client-user list is cache/WS data, assembled by the zef entity merger
 * from several endpoints with no version ordering, so it can only ever raise
 * a SUSPICION. Nothing destructive happens until a DIRECT `/user/info` read
 * — the authoritative source, and deliberately not a `loadUser()` action
 * round-trip, so there are no global success side effects or error dialogs —
 * agrees. Every failure mode (timeout, HTTP error, a session that moved on
 * mid-read, an unconfirmed suspicion) leaves the session untouched.
 */
@Injectable()
export class ClientUserRemovalEffect {

  // # Deps
  #store = inject(Store);
  #userEntity = inject(UserEntity);
  #clientUserEntity = inject(ClientUserEntity);
  #api = inject(UserBaseApi);

  // # Effects
  onClientUserRemovalVerified$ = createEffect(() => this.#clientUserEntity.list$().pipe(
    withLatestFrom(
      this.#userEntity.activeClientUser$,
      this.#userEntity.activeUser$,
      this.#store.pipe(select(selectUserBaseActiveClientUserId))
    ),
    filter(([ clientUsers, activeClientUser, activeUser ]) =>
      !!activeClientUser && !!activeUser && suspectsRemoval(clientUsers, activeClientUser)
    ),
    throttleTime(SUSPICION_THROTTLE_MS, asyncScheduler, { leading: true, trailing: true }),
    exhaustMap(([ , activeClientUser, activeUser, selectedClientUserId ]) => this.#api.load$().pipe(
      timeout(VERIFY_TIMEOUT_MS),
      withLatestFrom(
        this.#userEntity.activeUser$,
        this.#store.pipe(select(selectUserBaseActiveClientUserId))
      ),
      // The session must not have moved on while the read was in flight: a
      // logout or user switch invalidates the suspicion outright, and an org
      // switch would aim the wipe at the wrong membership.
      filter(([ , currentActiveUser, currentSelectedClientUserId ]) =>
        currentActiveUser?.id === activeUser.id
        && currentSelectedClientUserId === selectedClientUserId
      ),
      filter(([ userInfo ]) => confirmsRemoval(userInfo, {
        activeUserId: activeUser.id,
        activeClientId: activeClientUser.clientId
      })),
      mergeMap(([ , currentActiveUser ]) => this.#buildWipeSequence(currentActiveUser, selectedClientUserId)),
      // Fail safe: an unverifiable removal must never wipe. Silent — no
      // background error dialog — and the effect stays alive for the next
      // suspicion.
      catchError(() => EMPTY)
    ))
  ));

  // The exact wipe sequence formerly dispatched by `onClientUsersChanges$`
  // (app.effect.ts), moved verbatim.
  #buildWipeSequence(activeUser: User, removedClientUserId: string) {
    const { id, clientUserList } = activeUser;
    const clientUserListUpdated = clientUserList.filter((item) => item.id !== removedClientUserId);

    return [
      storeUserData(id),
      /**
       * Because it is not possible to subscribe to a WS channel for updates to UserEntity
       * (because of the missing clientId context), it is necessary to remove the obsolete record locally.
       */
      this.#userEntity.updateCache([ { id, clientUserList: clientUserListUpdated } ], {
        zefEntityMergeStrategy: {
          clientUserList: MergeStrategy.KeepNew
        }
      }),
      zefAddError(
        'login-no-accounts',
        'NO_ACTIVE_ACCOUNTS',
        'No active accounts found',
        undefined,
        undefined,
        'snack'
      ),
      zefGo(LOGIN_ROUTE, {}, { onSameUrlNavigation: 'reload' })
    ];
  }
}
